import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { userInitialsAsString } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import { fetchIsoNudgeOptions, sendIsoNudge } from '../../util/api';

import { H4, NamedLink, PrimaryButton } from '../../components';

import css from './WantedPanel.module.css';

const { Money } = sdkTypes;

/**
 * WantedPanel - vises i stedet for OrderPanel på "In Search Of"-annoncer.
 * En efterlysning kan ikke købes; i stedet inviterer panelet sælgere til at
 * sætte en matchende vare til salg, og viser efterlyserens budget.
 * Sælgere med en allerede-listet vare kan sende efterlyseren en "nudge"
 * (mail med foto+link) - menneskets genvej uden om auto-matcheren.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing efterlysnings-annoncen (inkl. author)
 * @param {string} props.marketplaceCurrency
 * @param {boolean} props.isOwnListing er den nuværende bruger efterlyseren
 * @param {boolean} props.isAuthenticated
 * @param {React.Node} props.authorLink link til efterlyserens profil
 * @param {React.Node} props.titleDesktop overskrift til desktop
 */
const WantedPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    isOwnListing,
    isAuthenticated,
    authorLink,
    titleDesktop,
  } = props;
  const intl = useIntl();

  // Nudge-tilstand: lukket → åben (henter egne varer) → sendt.
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeOptions, setNudgeOptions] = useState(null); // null = ikke hentet
  const [selectedId, setSelectedId] = useState(null);
  const [nudgeSending, setNudgeSending] = useState(false);
  const [nudgeSent, setNudgeSent] = useState(false);
  const [nudgeError, setNudgeError] = useState(null); // 'already' | 'generic'

  const price = listing?.attributes?.price;
  const hasBudget = price instanceof Money && typeof price.amount === 'number';
  const budgetFormatted = hasBudget ? formatMoney(intl, price) : null;

  // Nextmine: den der efterlyser vises kun med initialer.
  const requesterFirstName = userInitialsAsString(listing?.author, '') || 'them';

  const openNudge = () => {
    setNudgeOpen(true);
    setNudgeError(null);
    if (nudgeOptions === null) {
      fetchIsoNudgeOptions()
        .then(data => setNudgeOptions(data.listings || []))
        .catch(() => {
          setNudgeOptions([]);
          setNudgeError('generic');
        });
    }
  };

  const submitNudge = () => {
    if (!selectedId || nudgeSending) {
      return;
    }
    setNudgeSending(true);
    setNudgeError(null);
    sendIsoNudge({ requestId: listing?.id?.uuid, listingId: selectedId })
      .then(() => setNudgeSent(true))
      .catch(e => setNudgeError(e?.alreadyNudged ? 'already' : 'generic'))
      .finally(() => setNudgeSending(false));
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.panel}>
        <span className={css.wantedTag}>
          <FormattedMessage id="WantedPanel.tag" />
        </span>

        {titleDesktop}

        <div className={css.budgetRow}>
          <span className={css.budgetLabel}>
            <FormattedMessage id="WantedPanel.budgetLabel" />
          </span>
          <span className={css.budgetValue}>
            {budgetFormatted ? (
              <FormattedMessage id="WantedPanel.budgetValue" values={{ price: budgetFormatted }} />
            ) : (
              <FormattedMessage id="WantedPanel.budgetOpen" />
            )}
          </span>
        </div>

        {authorLink ? (
          <p className={css.requestedBy}>
            <FormattedMessage id="WantedPanel.requestedBy" values={{ author: authorLink }} />
          </p>
        ) : null}

        {isOwnListing ? (
          <p className={css.ownNote}>
            <FormattedMessage id="WantedPanel.ownNote" />
          </p>
        ) : (
          <>
            <NamedLink name="NewListingPage" className={css.ctaLink}>
              {/* className (IKKE rootClassName) - ellers mistes den fyldte oxblood-styling */}
              <PrimaryButton className={css.ctaButton} type="button">
                <FormattedMessage id="WantedPanel.listItCta" />
              </PrimaryButton>
            </NamedLink>
            <p className={css.helper}>
              <FormattedMessage id="WantedPanel.helper" />
            </p>

            {/* Nudge: kun for loggede brugere der ikke ejer efterlysningen */}
            {isAuthenticated ? (
              <div className={css.nudgeSection}>
                {nudgeSent ? (
                  <p className={css.nudgeSent}>
                    <FormattedMessage
                      id="WantedPanel.nudgeSent"
                      values={{ name: requesterFirstName }}
                    />
                  </p>
                ) : !nudgeOpen ? (
                  <button type="button" className={css.nudgeOpenButton} onClick={openNudge}>
                    <FormattedMessage
                      id="WantedPanel.nudgeButton"
                      values={{ name: requesterFirstName }}
                    />
                  </button>
                ) : (
                  <div className={css.nudgePicker}>
                    <p className={css.nudgeTitle}>
                      <FormattedMessage
                        id="WantedPanel.nudgePickTitle"
                        values={{ name: requesterFirstName }}
                      />
                    </p>
                    {nudgeOptions === null ? (
                      <p className={css.nudgeLoading}>
                        <FormattedMessage id="WantedPanel.nudgeLoading" />
                      </p>
                    ) : nudgeOptions.length === 0 ? (
                      <p className={css.nudgeEmpty}>
                        <FormattedMessage id="WantedPanel.nudgeNoListings" />
                      </p>
                    ) : (
                      <>
                        <div className={css.nudgeList}>
                          {nudgeOptions.map(l => (
                            <label key={l.id} className={css.nudgeOption}>
                              <input
                                type="radio"
                                name="nudgeListing"
                                value={l.id}
                                checked={selectedId === l.id}
                                onChange={() => setSelectedId(l.id)}
                              />
                              <span>{l.title}</span>
                            </label>
                          ))}
                        </div>
                        <PrimaryButton
                          className={css.nudgeSendButton}
                          type="button"
                          inProgress={nudgeSending}
                          disabled={!selectedId || nudgeSending}
                          onClick={submitNudge}
                        >
                          <FormattedMessage id="WantedPanel.nudgeSend" />
                        </PrimaryButton>
                      </>
                    )}
                    {nudgeError ? (
                      <p className={css.nudgeError}>
                        <FormattedMessage
                          id={
                            nudgeError === 'already'
                              ? 'WantedPanel.nudgeAlready'
                              : 'WantedPanel.nudgeError'
                          }
                        />
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className={css.nudgeCancel}
                      onClick={() => setNudgeOpen(false)}
                    >
                      <FormattedMessage id="WantedPanel.nudgeCancel" />
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Mobil: fast bund-CTA så "list it for sale" altid er synlig - samme
          mønster som Buy now-bjælken (OrderPanel .openOrderForm). */}
      {!isOwnListing ? (
        <div className={css.mobileCta}>
          <div className={css.mobileCtaBudget}>
            <span className={css.mobileCtaBudgetLabel}>
              <FormattedMessage id="WantedPanel.budgetLabel" />
            </span>
            <span className={css.mobileCtaBudgetValue}>
              {budgetFormatted ? (
                <FormattedMessage
                  id="WantedPanel.budgetValue"
                  values={{ price: budgetFormatted }}
                />
              ) : (
                <FormattedMessage id="WantedPanel.budgetOpenShort" />
              )}
            </span>
          </div>
          <NamedLink name="NewListingPage" className={css.mobileCtaLink}>
            <PrimaryButton className={css.mobileCtaButton} type="button">
              <FormattedMessage id="WantedPanel.listItCta" />
            </PrimaryButton>
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
};

export default WantedPanel;
